import { Icon } from '@iconify/react';
// material
import {
  Box,
  Card,
  Collapse,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery
} from '@mui/material';
import { filter } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import moment from 'moment';

//
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import Label from '../components/Label';
// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { ReportFilterSidebar } from '../components/sidebarFilter';
import { getReports } from '../fetch';
import { distanceConverter } from '../common/utils/unitConverter';
import {
  ReportClubListHead,
  ReportClubListToolbar,
  ReportClubOrderTimeline,
  ReportClubMap
} from '../components/reportClub';
import { useTranslation } from '../common/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import { summaryPilotAction } from '../store';
import {
  selectGroupSelectedId,
  selectSession,
  selectSummaryPilotArray,
  selectSummaryResultArray,
  selectUsersArray
} from '../createSelectorUtils';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (
    orderBy === 'toucher' ||
    orderBy === 'landing' ||
    orderBy === 'distance' ||
    orderBy === 'engineHours' ||
    orderBy === 'lastFlight'
  ) {
    if (parseInt(b[orderBy], 10) < parseInt(a[orderBy], 10)) {
      return -1;
    }
    if (parseInt(b[orderBy], 10) > parseInt(a[orderBy], 10)) {
      return 1;
    }
  } else {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
  }

  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_summary) =>
        (_summary.name !== null &&
          _summary.name.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_summary.prenom !== null &&
          _summary.prenom.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_summary.lastFlight !== null &&
          moment
            .unix(_summary.lastFlight / 1000)
            .format('DD/MM/YYYY HH:MM')
            .indexOf(query.toLowerCase()) !== -1) ||
        (_summary.engineHours !== null && _summary.engineHours.indexOf(query) !== -1) ||
        (_summary.distance !== null &&
          _summary.distance.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_summary.landing !== null && _summary.landing.indexOf(query) !== -1) ||
        (_summary.toucher !== null && _summary.toucher.indexOf(query) !== -1)
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function ReportClub() {
  const t = useTranslation();
  const Up = useMediaQuery((theme) => theme.breakpoints.up('sm'));
  const dispatch = useDispatch();

  const session = useSelector(selectSession);
  const groupId = useSelector(selectGroupSelectedId);
  // ----------------------------------------------------------------------

  const hiddenDown = useMediaQuery((theme) => theme.breakpoints.between('xs', 'sm'));

  const [tableHead, setTableHead] = useState([]);

  useEffect(() => {
    const INITIAL_TABLE_HEAD = [
      { id: 'name', label: t('RC_LastName'), alignRight: false, display: undefined },
      { id: 'prenom', label: t('RC_FirstName'), alignRight: false, display: undefined },
      { id: 'lastFlight', label: t('RC_LastFlight'), alignRight: false, display: undefined },
      { id: 'engineHours', label: t('RC_Hours'), alignRight: false, display: undefined },
      { id: 'distance', label: t('RC_Distance'), alignRight: false, display: undefined },
      { id: 'landing', label: t('RC_Landings'), alignRight: false, display: undefined },
      { id: 'toucher', label: t('DB_Touché'), alignRight: false, display: undefined },
      { id: 'totalPoint', label: t('Total_Point'), alignRight: false, display: undefined },
      { id: '', label: '', alignRight: false }
    ];

    if (hiddenDown) {
      const newTableHead = INITIAL_TABLE_HEAD.map((item) =>
        item.id !== 'name' && item.id !== 'prenom' && item.id !== 'lastFlight'
          ? { ...item, display: 'none' }
          : item
      );
      setTableHead(newTableHead);
    } else {
      setTableHead(INITIAL_TABLE_HEAD);
    }
  }, [hiddenDown, t]);
  // ----------------------------------------------------------------------
  const [openFilter, setOpenFilter] = useState(false);
  const [openProgress, setOpenProgress] = useState(false);

  const formik = useFormik({
    initialValues: {
      group: !session.administrator
        ? { id: groupId }
        : JSON.parse(window.localStorage.getItem('reportClubFilterGroup')) || null,
      device: null,
      user: null,
      period: JSON.parse(window.localStorage.getItem('reportClubFilterPeriod')) || 'last3Month',
      from: window.localStorage.getItem('reportClubFilterFrom')
        ? moment(new Date(window.localStorage.getItem('reportClubFilterFrom')))
        : moment().subtract(1, 'day'),
      to: window.localStorage.getItem('reportClubFilterTo')
        ? moment(new Date(window.localStorage.getItem('reportClubFilterTo')))
        : moment()
    },
    onSubmit: async (values) => {
      setOpenProgress(true);
      let selectedFrom;
      let selectedTo;
      switch (values.period) {
        case 'today':
          selectedFrom = moment().startOf('day');
          selectedTo = moment().endOf('day');
          break;
        case 'yesterday':
          selectedFrom = moment().subtract(1, 'day').startOf('day');
          selectedTo = moment().subtract(1, 'day').endOf('day');
          break;
        case 'thisWeek':
          selectedFrom = moment().startOf('week');
          selectedTo = moment().endOf('week');
          break;
        case 'previousWeek':
          selectedFrom = moment().subtract(1, 'week').startOf('week');
          selectedTo = moment().subtract(1, 'week').endOf('week');
          break;
        case 'thisMonth':
          selectedFrom = moment().startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'previousMonth':
          selectedFrom = moment().subtract(1, 'month').startOf('month');
          selectedTo = moment().subtract(1, 'month').endOf('month');
          break;
        case 'last3Month':
          selectedFrom = moment().subtract(3, 'month').startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'last6Month':
          selectedFrom = moment().subtract(6, 'month').startOf('month');
          selectedTo = moment().endOf('month');
          break;
        case 'thisYear':
          selectedFrom = moment().startOf('year');
          selectedTo = moment().endOf('month');
          break;
        default:
          selectedFrom = values.from.set('hour', 0).set('minute', 0).set('second', 0).utc(true);
          selectedTo = values.to.set('hour', 23).set('minute', 59).set('second', 59).utc(true);
          break;
      }

      window.localStorage.setItem('reportClubFilterGroup', JSON.stringify(values.group));
      window.localStorage.setItem('reportClubFilterPeriod', JSON.stringify(values.period));
      window.localStorage.setItem(
        'reportClubFilterFrom',
        moment(values.from, moment.HTML5_FMT.DATE)
      );
      window.localStorage.setItem('reportClubFilterTo', moment(values.to, moment.HTML5_FMT.DATE));

      const responseSummaryPilote = await getReports(
        '/summary/pilot',
        0,
        values.group !== null ? values.group.id : 0,
        0,
        selectedFrom.toISOString(),
        selectedTo.toISOString(),
        false
      );
      if (responseSummaryPilote.ok) {
        const result = await responseSummaryPilote.json();
        dispatch(summaryPilotAction.update(await result));
        const newUser = users.map((user) => {
          const lastFlight = result.reduce((result, report) => {
            if (user.id === report.piloteId) {
              result = moment(report.lastVol).format('x');
            }
            if (result === 'Invalid date') {
              result = 0;
            }
            return result;
          }, '');

          const engineHours = result.reduce((result, report) => {
            if (user.id === report.piloteId) {
              result = moment.duration(report.engineHours).asHours().toFixed(0);
            }
            return result;
          }, ' ');
          const distance = result.reduce((result, report) => {
            if (user.id === report.piloteId) {
              result = distanceConverter(report.distance / 1000, session.uniteDistance);
            }
            return result;
          }, ' ');
          const landing = parseInt(
            result.reduce((result, report) => {
              if (user.id === report.piloteId) {
                result = report.atterrissages;
              }
              return result;
            }, 0),
            10
          ).toString();

          const toucher = parseInt(
            result.reduce((result, report) => {
              if (user.id === report.piloteId) {
                result = report.touchers;
              }
              return result;
            }, 0),
            10
          ).toString();

          const totalPoint = parseInt(
            result.reduce((result, report) => {
              if (user.id === report.piloteId) {
                result = report.totalPoint;
              }
              return result;
            }, 0),
            10
          ).toString();

          return {
            ...user,
            lastFlight,
            engineHours,
            distance,
            landing,
            toucher,
            totalPoint
          };
        });

        dispatch(summaryPilotAction.updateResult(newUser));
      }

      setOpenFilter(false);
      setOpenProgress(false);
    }
  });

  const { resetForm, handleSubmit, getFieldProps } = formik;

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleResetFilter = () => {
    window.localStorage.setItem('reportClubFilterGroup', null);
    window.localStorage.setItem('reportClubFilterPeriod', JSON.stringify('today'));
    window.localStorage.setItem('reportClubFilterFrom', moment().subtract(1, 'hour'));
    window.localStorage.setItem('reportClubFilterTo', moment());
    handleSubmit();
    resetForm();
  };

  // ----------------------------------------------------------------------
  const usersArray = useSelector(selectUsersArray);
  const users =
    getFieldProps('group').value === null
      ? []
      : usersArray.filter(
          (user) =>
            user.permission &&
            user.permission.find((item) => item.groupid === getFieldProps('group').value.id)
        );
  const summaryPilot = useSelector(selectSummaryPilotArray);
  const summaryResult = useSelector(selectSummaryResultArray);
  // ----------------------------------------------------------------------

  // ----------------------------------------------------------------------
  const [openCollapse, setOpenCollapse] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - summaryResult.length) : 0;

  const filteredSummary = applySortFilter(summaryResult, getComparator(order, orderBy), filterName);

  const isSummaryNotFound = filteredSummary.length === 0;

  useEffectAsync(async () => {
    if (summaryPilot.length === 0 || summaryResult.length === 0) {
      await handleSubmit();
    }
  }, [groupId, users.length]);

  return (
    <Page title={t('RC_Title')} openProgress={openProgress}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0}>
          <Typography variant="h4" gutterBottom>
            {t('reportClub')}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap-reverse"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: 5 }}
        >
          <Stack direction="row" spacing={1} flexShrink={0} sx={{ my: 1 }}>
            <ReportFilterSidebar
              formik={formik}
              isOpenFilter={openFilter}
              onResetFilter={handleResetFilter}
              onOpenFilter={handleOpenFilter}
              onCloseFilter={handleCloseFilter}
              onSaveFilter={handleSubmit}
              clubFilter
            />
          </Stack>
        </Stack>

        <Card>
          <ReportClubListToolbar filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 300 }}>
              <Table>
                <ReportClubListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHead}
                  rowCount={summaryResult.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredSummary
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const {
                        id,
                        name,
                        prenom,
                        lastFlight,
                        engineHours,
                        distance,
                        landing,
                        toucher,
                        totalPoint
                      } = row;

                      return (
                        <Fragment key={id}>
                          <TableRow
                            hover
                            key={`first${id}`}
                            sx={{ '& > *': { borderBottom: 'unset' } }}
                            onClick={() => setOpenCollapse(id === openCollapse ? false : id)}
                          >
                            <TableCell sx={{ display: tableHead[0].display }}>{name}</TableCell>
                            <TableCell align="left" sx={{ display: tableHead[1].display }}>
                              {prenom}
                            </TableCell>
                            <TableCell align="left" sx={{ display: tableHead[2].display }}>
                              {lastFlight !== 0
                                ? moment.unix(lastFlight / 1000).format('DD/MM/YYYY HH:mm')
                                : ''}
                            </TableCell>
                            <TableCell
                              align="left"
                              component="th"
                              scope="row"
                              sx={{ display: tableHead[3].display }}
                            >
                              <Label variant="ghost" color="success">
                                {engineHours}
                              </Label>
                            </TableCell>
                            <TableCell align="left" sx={{ display: tableHead[4].display }}>
                              <Label variant="ghost" color="success">
                                {`${distance} ${session.uniteDistance}`}
                              </Label>
                            </TableCell>
                            <TableCell align="left" sx={{ display: tableHead[5].display }}>
                              <Label variant="ghost" color="error">
                                {landing}
                              </Label>
                            </TableCell>
                            <TableCell align="left" sx={{ display: tableHead[6].display }}>
                              <Label variant="ghost" color="error">
                                {toucher}
                              </Label>
                            </TableCell>
                            <TableCell align="left" sx={{ display: tableHead[7].display }}>
                              <Label variant="ghost" color="warning">
                                {totalPoint}
                              </Label>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() => setOpenCollapse(id === openCollapse ? false : id)}
                              >
                                {openCollapse === id ? (
                                  <Icon icon="ic:baseline-keyboard-arrow-up" />
                                ) : (
                                  <Icon icon="ic:baseline-keyboard-arrow-down" />
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {openCollapse === id && (
                            <TableRow hover key={`more${id}`}>
                              <TableCell
                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                colSpan={Up ? 12 : 3}
                              >
                                <Collapse in={openCollapse === id} timeout="auto" unmountOnExit>
                                  <Box sx={{ margin: 1 }}>
                                    <Grid container spacing={1}>
                                      <Grid item xs={12} sm={6} md={6} lg={4}>
                                        <ReportClubOrderTimeline
                                          summaryPilot={summaryPilot}
                                          id={id}
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={6} md={6} lg={8}>
                                        <ReportClubMap
                                          summary={summaryPilot.filter(
                                            (item) => openCollapse === item.piloteId
                                          )}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
                {isSummaryNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={12} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 50, 100, 250, 500]}
            component="div"
            count={filteredSummary.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
